import React, { useEffect, useRef, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { vectorBasemapLayer } from "esri-leaflet-vector";
import "maplibre-gl/dist/maplibre-gl.css";
import "@geoman-io/leaflet-geoman-free";
import "@geoman-io/leaflet-geoman-free/dist/leaflet-geoman.css";
import * as turf from "@turf/turf";
import { toPng } from "html-to-image";
import jsPDF from "jspdf";
import api from "../api";
import { TreatmentArea, NoticeLayout, loadTreatmentAreas, upsertTreatmentArea, deleteTreatmentArea, areaLabel, isoToMdy, mdyToIso } from "../lib/treatmentArea";
import { getSubgridLayerId } from "../lib/subgridLayer";
import { getTreatmentLayerId } from "../lib/treatmentLayer";

type Mode = "pick" | "reshape" | "draw";
type Reason = "wnv" | "abundance";

// Exact footer copy from the District's print template
const BODY: Record<Reason, string> = {
  wnv: "Mosquitoes collected in this area have been tested positive for West Nile Virus (WNV). Truck ULV treatments target mosquito species most active at night and known to carry WNV. These applications have little effect on daytime biting mosquitoes.",
  abundance: "High numbers of mosquitoes have been detected in this area. Truck ULV treatments will be made to reduce the number of mosquitoes. These applications have little effect on daytime biting mosquitoes, as treatments target the species most active at night.",
};

// Default text seeded into the editable "Custom" footer
const CUSTOM_DEFAULT = "West Nile Virus (WNV) activity has been detected in this area. Truck ULV treatments target mosquito species most active at night and known to carry WNV. These applications have little effect on daytime biting mosquitoes.";

// White margin (CSS px, scales with export resolution) drawn around the exported notice
const EXPORT_MARGIN_PX = 16;

// How much of a subgrid must fall inside the treatment boundary for that subgrid to count as
// covered. A reshaped area often straddles two cells (half of each), and the clicked-cell list
// then under-reports what was actually treated — so coverage is derived from the geometry.
// 25% keeps real halves and drops the few-percent slivers left by boundary drift.
const COVERAGE_THRESHOLD = 0.25;

// ArcGIS API key (basemap-scoped) for Esri vector basemaps. Set REACT_APP_ARCGIS_API_KEY.
const ARCGIS_API_KEY = process.env.REACT_APP_ARCGIS_API_KEY ?? "";

// Raster tile fallbacks (used for the export/notice map, and for the main map when no API key is set)
const RASTER = {
  worldStreet: {
    url: "https://server.arcgisonline.com/ArcGIS/rest/services/World_Street_Map/MapServer/tile/{z}/{y}/{x}",
    opts: { maxZoom: 19, attribution: "", crossOrigin: "anonymous" } as L.TileLayerOptions,
  },
  worldImagery: {
    url: "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
    opts: { maxZoom: 19, attribution: "", crossOrigin: "anonymous" } as L.TileLayerOptions,
  },
};

// Main-map basemaps. Vector ones are Esri hosted vector basemaps (need the API key);
// each carries a raster `fallback` used only when no API key is present.
const BASEMAPS = {
  // NOTE: Esri has retired the 'osm/*' styles (last updated 2024) and recommends 'open/*'.
  // Not migrated yet: the replacement style id needs verifying against a working API key
  // before it ships, since a wrong id breaks the basemap outright.
  streets: { label: "Streets", kind: "vector", style: "osm/standard", fallback: RASTER.worldStreet },
  imagery: { label: "Imagery", kind: "raster", raster: RASTER.worldImagery, fallback: RASTER.worldImagery },
  hybrid: { label: "Hybrid", kind: "vector", style: "arcgis/imagery", fallback: RASTER.worldImagery },
} as const;
type BasemapKey = keyof typeof BASEMAPS;

// Build a basemap layer. `preserveDrawingBuffer` is required on the export/notice map so
// html-to-image can read the maplibre WebGL canvas; the main interactive map doesn't need it.
function makeBasemap(key: BasemapKey, preserveDrawingBuffer = false): L.Layer {
  const bm = BASEMAPS[key];
  if (bm.kind === "vector" && ARCGIS_API_KEY) {
    return (vectorBasemapLayer as any)(bm.style, { apiKey: ARCGIS_API_KEY, preserveDrawingBuffer });
  }
  const r = bm.kind === "raster" ? bm.raster : bm.fallback;
  return L.tileLayer(r.url, { ...r.opts, ...(preserveDrawingBuffer ? { detectRetina: true } : {}) });
}
const makeBaseTile = (key: BasemapKey): L.Layer => makeBasemap(key, false);
const makeNoticeBase = (key: BasemapKey): L.Layer => makeBasemap(key, true);
const sendBack = (lyr: any) => { if (typeof lyr?.bringToBack === "function") lyr.bringToBack(); };

// Subgrid styles from the prototype
const BASE_STYLE: L.PathOptions = { color: "#8b958c", weight: 1, opacity: 0.7, fill: true, fillOpacity: 0, fillColor: "#000" };
const HOVER_STYLE: L.PathOptions = { color: "#5b9e3f", weight: 1.5, opacity: 0.9, fill: true, fillOpacity: 0.1, fillColor: "#5b9e3f" };
const SEL_STYLE: L.PathOptions = { color: "#1c5fce", weight: 1.6, opacity: 1, fill: true, fillOpacity: 0.45, fillColor: "#bcd6e8" };
const TREATED_STYLE: L.PathOptions = { color: "#2E7D32", weight: 2, opacity: 1, fill: true, fillOpacity: 0.45, fillColor: "#4CAF50" };
const AREA_STYLE: L.PathOptions = { color: "#1c5fce", weight: 2.5, fillColor: "#bcd6e8", fillOpacity: 0.2 };
// Saved-but-not-active areas, drawn together for the end-of-week review pass. Dashed violet so
// the area currently being edited (AREA_STYLE, solid blue) still reads as the active one.
const SAVED_STYLE: L.PathOptions = { color: "#7c3aed", weight: 2, dashArray: "5,4", fillColor: "#a78bfa", fillOpacity: 0.12 };

interface Road {
  id: string;
  text: string;
  rotation: number;
  marker: L.Marker;
  apply: () => void;
}

function roadIcon(text: string, rotation = 0) {
  const esc = text.replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c] as string));
  return L.divIcon({
    className: "",
    html: `<div style="background:#eef0f2;color:#111;font-weight:700;font-size:12px;font-family:'PT Sans',sans-serif;padding:1px 5px;border-radius:2px;white-space:nowrap;box-shadow:0 1px 2px rgba(0,0,0,.35);cursor:move;border:1px solid rgba(0,0,0,.25);display:inline-block;transform:rotate(${rotation}deg);transform-origin:center center;">${esc}</div>`,
    iconSize: undefined as any,
    iconAnchor: [0, 0],
  });
}

export default function BuilderPage() {
  const mapDivRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<L.Map | null>(null);
  const noticeMapDivRef = useRef<HTMLDivElement>(null);
  const noticeMapRef = useRef<L.Map | null>(null);
  const noticeAreaRef = useRef<L.GeoJSON | null>(null);
  const subgridLayerRef = useRef<L.GeoJSON | null>(null);
  const areaLayerRef = useRef<L.Polygon | null>(null);
  const selectedRef = useRef<Set<string>>(new Set());
  const cellsRef = useRef<Record<string, GeoJSON.Feature>>({});
  const roadsRef = useRef<Road[]>([]);
  const positivesLayerRef = useRef<L.LayerGroup | null>(null);
  const noticeRef = useRef<HTMLDivElement>(null);
  const exportRef = useRef<HTMLDivElement>(null);
  const modeRef = useRef<Mode>("pick");
  const customGeomRef = useRef(false); // true once the boundary was reshaped/drawn free-form
  const overlayGroupsRef = useRef<Record<string, { main: L.GeoJSON; notice: L.GeoJSON }>>({});
  const noticePositivesRef = useRef<L.LayerGroup | null>(null);
  const poolLabelsRef = useRef<L.LayerGroup | null>(null);
  const noticePoolLabelsRef = useRef<L.LayerGroup | null>(null);
  const baseTileRef = useRef<L.Layer | null>(null);
  const noticeBaseTileRef = useRef<L.Layer | null>(null);
  const allAreasLayerRef = useRef<L.LayerGroup | null>(null);

  const [mode, setMode] = useState<Mode>("pick");
  const [selected, setSelected] = useState<string[]>([]);
  // Subgrids the boundary actually covers (>= COVERAGE_THRESHOLD), derived from geometry.
  // Drives the notice's "Subgrid/s" line and the GridLabels written to AGOL.
  const [covered, setCovered] = useState<string[]>([]);
  const [acres, setAcres] = useState(0);
  const [loadStatus, setLoadStatus] = useState("Loading subgrids…");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [timeWindow, setTimeWindow] = useState("8:30 PM - 12:00 AM");
  const [customWindow, setCustomWindow] = useState(false);
  const [customStart, setCustomStart] = useState("8:30 PM");
  const [customEnd, setCustomEnd] = useState("12:00 AM");
  // Ordered by start time, then end. 9:00 PM - 12:00 AM was replaced by
  // 8:00 PM - 12:00 AM on 2026-08-20.
  const WINDOW_TEMPLATES = ["8:00 PM - 10:00 PM", "8:00 PM - 12:00 AM", "8:30 PM - 12:00 AM", "9:00 PM - 11:00 PM"];
  const [reason, setReason] = useState<Reason>("wnv");
  const [customBody, setCustomBody] = useState<string | null>(null);
  const [roadChips, setRoadChips] = useState<{ id: string; text: string }[]>([]);
  const [roadInput, setRoadInput] = useState("");
  const [busy, setBusy] = useState("");
  const [showPositives, setShowPositives] = useState(true);
  const [showPoolLabels, setShowPoolLabels] = useState(false);
  const [moveApprox, setMoveApprox] = useState(false);
  const approxMarkersRef = useRef<L.Marker[]>([]);
  const [basemap, setBasemap] = useState<BasemapKey>("streets");
  const [mapTemplate, setMapTemplate] = useState<"community" | "spray">("community");
  const [savedAreas, setSavedAreas] = useState<TreatmentArea[]>(loadTreatmentAreas);
  const [editingId, setEditingId] = useState<string | null>(null);
  // Mirrored into a ref so the AGOL sync can check it without being rebuilt
  // on every selection change.
  const editingIdRef = useRef<string | null>(null);
  const [showAllAreas, setShowAllAreas] = useState(false);
  // Why the vector basemap isn't drawing, when it isn't. Deliberately NOT a fallback to
  // raster: the Esri basemaps are the ones wanted on notices, so a failure must be visible
  // rather than silently substituted. Empty string = basemap is fine.
  const [basemapIssue, setBasemapIssue] = useState("");
  // Set when the layer can't be re-read, so stale chip statuses announce themselves rather
  // than quietly claiming an area is still scheduled after it was cleared elsewhere.
  const [syncWarning, setSyncWarning] = useState("");
  const [positiveCount, setPositiveCount] = useState(0);
  const [overlays, setOverlays] = useState<{ name: string; layerId: string; sublayer?: number; on: boolean; loading?: boolean; error?: string }[]>(() => {
    const DEFAULT_OVERLAYS = [
      { name: "Streets", layerId: "66d53bd3197a40ab8e7cbc480dccd326", on: false },
      { name: "TRS", layerId: "2531b1151ebd4cb9b90ef91b33811d0b", on: false },
      { name: "Resident Notices", layerId: "49262609faee4515bad3b18b0b854e9b", sublayer: 31, on: false },
    ];
    try {
      const stored = JSON.parse(localStorage.getItem("pioneer_overlay_layers") || "[]");
      if (Array.isArray(stored) && stored.length) {
        // Merge in any defaults the stored list doesn't have yet; always start unchecked
        const ids = new Set(stored.map((o: any) => o.layerId));
        return [...stored, ...DEFAULT_OVERLAYS.filter((d) => !ids.has(d.layerId))].map((o: any) => ({ ...o, on: false }));
      }
    } catch {}
    return DEFAULT_OVERLAYS;
  });
  // The spray route layers, offered as overlays once they are published. The
  // ids live in the backend environment rather than here, so publishing a
  // layer is a configuration change and not a release -- and the Spray Routes
  // card reads the same list, so the two cannot disagree about which layers
  // exist.
  useEffect(() => {
    let alive = true;
    api
      .get("/spray-route-layers")
      .then((r) => {
        if (!alive) return;
        const found = (r.data?.layers ?? []) as { item_id: string; name: string }[];
        if (!found.length) return;
        setOverlays((current) => {
          const have = new Set(current.map((o) => o.layerId));
          const added = found
            .filter((l) => !have.has(l.item_id))
            .map((l) => ({ name: l.name, layerId: l.item_id, on: false }));
          return added.length ? [...current, ...added] : current;
        });
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);

  const [showSubgrids, setShowSubgrids] = useState(true);
  const [overlayName, setOverlayName] = useState("");
  const [overlayId, setOverlayId] = useState("");

  const persistOverlays = (next: typeof overlays) => {
    setOverlays(next);
    localStorage.setItem("pioneer_overlay_layers", JSON.stringify(next.map(({ name, layerId, sublayer, on }) => ({ name, layerId, sublayer, on }))));
  };

  const overlayStyle = (feature?: GeoJSON.Feature): L.PathOptions => {
    const t = feature?.geometry?.type || "";
    if (t.includes("LineString")) return { color: "#777", weight: 1, opacity: 0.8 };
    return { color: "#b8860b", weight: 1, fill: true, fillOpacity: 0.05, fillColor: "#b8860b" };
  };

  const buildOverlayGroup = (geojson: GeoJSON.FeatureCollection, withPopups: boolean) =>
    L.geoJSON(geojson, {
      style: overlayStyle,
      pointToLayer: (_f, latlng) =>
        L.circleMarker(latlng, { radius: withPopups ? 4 : 3, color: "#6b21a8", weight: 1.5, fillColor: "#a855f7", fillOpacity: 0.7 }),
      onEachFeature: withPopups
        ? (feature, lyr) => {
            // Click any overlay feature to inspect its attributes
            const props = feature.properties || {};
            const rows = Object.entries(props)
              .filter(([k, v]) => v !== null && v !== "" && typeof v !== "object" && !/^shape_/i.test(k))
              .map(
                ([k, v]) =>
                  `<tr><td style="color:#8a948c;padding:1px 10px 1px 0">${k}</td><td style="font-family:'Spline Sans Mono',monospace">${String(v)}</td></tr>`
              )
              .join("");
            if (rows) lyr.bindPopup(`<table style="font:12px 'Public Sans',sans-serif;border-collapse:collapse">${rows}</table>`, { maxHeight: 260 });
          }
        : undefined,
    });

  const toggleOverlay = async (layerId: string, on: boolean) => {
    const map = mapRef.current;
    const nm = noticeMapRef.current;
    if (!map) return;
    setOverlays((os) => os.map((o) => (o.layerId === layerId ? { ...o, on } : o)));
    const pair = overlayGroupsRef.current[layerId];
    if (!on) {
      if (pair) {
        map.removeLayer(pair.main);
        nm?.removeLayer(pair.notice);
      }
      return;
    }
    if (pair) {
      pair.main.addTo(map);
      if (nm) pair.notice.addTo(nm);
      return;
    }
    setOverlays((os) => os.map((o) => (o.layerId === layerId ? { ...o, loading: true } : o)));
    try {
      const sublayer = overlays.find((o) => o.layerId === layerId)?.sublayer;
      const res = await api.post("/get-layer-geojson", { layer_id: layerId, sublayer });
      // One copy for the editor map (with popups), one mirrored onto the notice layout
      const main = buildOverlayGroup(res.data.geojson, true);
      const notice = buildOverlayGroup(res.data.geojson, false);
      overlayGroupsRef.current[layerId] = { main, notice };
      if (mapRef.current) main.addTo(mapRef.current);
      if (noticeMapRef.current) notice.addTo(noticeMapRef.current);
      setOverlays((os) => os.map((o) => (o.layerId === layerId ? { ...o, loading: false, error: undefined } : o)));
    } catch (err: any) {
      setOverlays((os) =>
        os.map((o) => (o.layerId === layerId ? { ...o, on: false, loading: false, error: err?.response?.data?.message || err.message } : o))
      );
    }
  };

  const cellStyle = useCallback((feature?: GeoJSON.Feature): L.PathOptions => {
    const label = feature?.properties?.GridLabel;
    // Once the boundary is free-form, stop highlighting the source cells (the
    // reshaped polygon is the visual); gridlabels stay tracked for mark-treated.
    if (label && selectedRef.current.has(label) && !customGeomRef.current) return SEL_STYLE;
    if (feature?.properties?.Status === "Treated") return TREATED_STYLE;
    return BASE_STYLE;
  }, []);

  /** Subgrids the finished boundary actually covers, at COVERAGE_THRESHOLD or more.
   *
   *  Not the same as the clicked cells: reshaping to centre an area on a boundary pool leaves
   *  the area half in each of two subgrids, while only one was ever clicked. Mark-treated and
   *  the notice's "Subgrid/s" line both need what the polygon really covers.
   *  Bbox-prefiltered, since the cell cache holds every subgrid in the district. */
  const coveredGridlabels = (geom: any): string[] => {
    if (!geom) return [];
    let areaFeat: any;
    let ab: number[];
    try {
      areaFeat = turf.feature(geom);
      ab = turf.bbox(areaFeat);
    } catch {
      return [];
    }
    const out: string[] = [];
    for (const [label, cell] of Object.entries(cellsRef.current)) {
      try {
        const cb = turf.bbox(cell as any);
        if (cb[2] < ab[0] || ab[2] < cb[0] || cb[3] < ab[1] || ab[3] < cb[1]) continue;
        const inter = turf.intersect(turf.featureCollection([areaFeat, cell as any]));
        if (!inter) continue;
        const cellArea = turf.area(cell as any);
        if (cellArea > 0 && turf.area(inter as any) / cellArea >= COVERAGE_THRESHOLD) out.push(label);
      } catch {}
    }
    return out.sort();
  };

  // Mirror the current treatment polygon onto the notice mini-map (print style: light blue)
  const renderNotice = useCallback(() => {
    const nm = noticeMapRef.current;
    if (!nm) return;
    if (noticeAreaRef.current) {
      nm.removeLayer(noticeAreaRef.current);
      noticeAreaRef.current = null;
    }
    const gj = areaLayerRef.current?.toGeoJSON();
    if (!gj) {
      setCovered([]);
      return;
    }
    // Recompute real subgrid coverage whenever the boundary changes
    setCovered(coveredGridlabels((gj as any).geometry));
    const layer = L.geoJSON(gj, { style: { color: "#7fa8c9", weight: 1.5, fillColor: "#cfe0ea", fillOpacity: 0.25 } }).addTo(nm);
    noticeAreaRef.current = layer;
    try {
      nm.fitBounds(layer.getBounds().pad(0.03), { animate: false });
    } catch {}
    setTimeout(() => nm.invalidateSize(), 30);
  }, []);

  const rebuildArea = useCallback(() => {
    const map = mapRef.current;
    if (!map) return;
    if (areaLayerRef.current) {
      map.removeLayer(areaLayerRef.current);
      areaLayerRef.current = null;
    }
    const feats = Array.from(selectedRef.current)
      .map((l) => cellsRef.current[l])
      .filter(Boolean);
    if (!feats.length) {
      setAcres(0);
      renderNotice();
      return;
    }
    let union: any = feats[0];
    for (let i = 1; i < feats.length; i++) {
      try {
        union = turf.union(turf.featureCollection([union, feats[i]] as any));
      } catch {}
    }
    if (!union) return;
    setAcres(Math.round(turf.area(union) / 4046.856));
    const layer = L.geoJSON(union, { style: AREA_STYLE, interactive: false });
    const poly = layer.getLayers()[0] as L.Polygon;
    poly.addTo(map);
    areaLayerRef.current = poly;
    customGeomRef.current = false;
    if (modeRef.current === "reshape") poly.pm.enable({ snappable: true });
    attachEditEvents(poly);
    renderNotice();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [renderNotice]);

  // Live-sync acres + notice while the boundary is being reshaped
  const attachEditEvents = (poly: L.Polygon) => {
    const sync = () => {
      customGeomRef.current = true;
      subgridLayerRef.current?.setStyle(cellStyle as any); // un-highlight source cells

      try {
        setAcres(Math.round(turf.area(poly.toGeoJSON() as any) / 4046.856));
      } catch {}
      renderNotice();
    };
    poly.on("pm:edit", sync);
    poly.on("pm:markerdragend", sync);
    poly.on("pm:vertexremoved", sync);
  };

  // Maps init
  useEffect(() => {
    if (!mapDivRef.current || mapRef.current || !noticeMapDivRef.current) return;
    const map = L.map(mapDivRef.current, { zoomControl: true }).setView([36.7, -119.7], 12);
    baseTileRef.current = makeBaseTile("streets").addTo(map);
    sendBack(baseTileRef.current);
    mapRef.current = map;

    const nm = L.map(noticeMapDivRef.current, {
      zoomControl: false,
      attributionControl: false,
      dragging: true,
      scrollWheelZoom: true,
      zoomSnap: 0.25,
      zoomDelta: 0.25,
      wheelPxPerZoomLevel: 120,
    }).setView([36.7, -119.7], 12);
    // Always-retina tiles at double density: print-sharp labels in the export
    noticeBaseTileRef.current = makeNoticeBase("streets").addTo(nm);
    noticeMapRef.current = nm;

    api
      .post("/get-subgrids-geojson", { layer_id: getSubgridLayerId() || undefined })
      .then((res) => {
        if (mapRef.current !== map) return; // map was torn down (StrictMode remount / navigation)
        const gj = res.data.geojson as GeoJSON.FeatureCollection;
        gj.features.forEach((f) => {
          if (f.properties?.GridLabel) cellsRef.current[f.properties.GridLabel] = f;
        });
        const layer = L.geoJSON(gj, {
          style: cellStyle,
          onEachFeature: (feature, lyr) => {
            const label = feature.properties?.GridLabel;
            if (label) lyr.bindTooltip(label, { direction: "center", className: "gridlabel", opacity: 1 });
            lyr.on("mouseover", () => {
              if (modeRef.current === "pick" && label && !selectedRef.current.has(label)) (lyr as L.Path).setStyle(HOVER_STYLE);
            });
            lyr.on("mouseout", () => {
              if (label && !selectedRef.current.has(label)) (lyr as L.Path).setStyle(cellStyle(feature));
            });
            lyr.on("click", () => {
              if (modeRef.current !== "pick" || !label) return;
              // After a free-form reshape/draw, picking a cell restarts from subgrids
              if (customGeomRef.current) {
                selectedRef.current.clear();
                customGeomRef.current = false;
                subgridLayerRef.current?.setStyle(cellStyle as any);
              }
              if (selectedRef.current.has(label)) selectedRef.current.delete(label);
              else selectedRef.current.add(label);
              setSelected(Array.from(selectedRef.current).sort());
              (lyr as L.Path).setStyle(cellStyle(feature));
              rebuildArea();
            });
          },
        }).addTo(map);
        subgridLayerRef.current = layer;
        try {
          map.fitBounds(layer.getBounds(), { padding: [20, 20] });
        } catch {}
        setLoadStatus(`${gj.features.length} subgrids loaded`);

        // Pre-select this week's positive subgrids (?subgrids=a,b or Disease Map Generation handoff)
        let preset: string[] = (new URLSearchParams(window.location.search).get("subgrids") || "")
          .split(",").map((s) => s.trim()).filter(Boolean);
        if (!preset.length) {
          try {
            preset = JSON.parse(localStorage.getItem("pioneer_positive_subgrids") || "[]");
          } catch {}
        }
        const valid = preset.filter((g) => cellsRef.current[g]);
        if (valid.length) {
          valid.forEach((g) => selectedRef.current.add(g));
          setSelected(Array.from(selectedRef.current).sort());
          layer.eachLayer((lyr: any) => {
            const gl = lyr.feature?.properties?.GridLabel;
            if (gl && selectedRef.current.has(gl)) lyr.setStyle(SEL_STYLE);
          });
          rebuildArea();
          try {
            map.fitBounds(L.geoJSON({ type: "FeatureCollection", features: valid.map((g) => cellsRef.current[g]) } as any).getBounds().pad(1.2));
          } catch {}
        }
      })
      .catch((err) => {
        setLoadStatus(
          `Subgrid load failed: ${err?.response?.data?.message || err.message}. Log in to AGOL first, or draw the area manually.`
        );
      });

    // This week's disease-positive locations (from Disease Map Generation), toggleable
    try {
      const samples: { pool?: string; x: number; y: number; subgrid?: string; diseases?: string[]; precision?: string; comments?: string }[] =
        JSON.parse(localStorage.getItem("pioneer_positive_samples") || "[]");
      // Samples may be lon/lat or Web Mercator (EPSG:3857) — convert if out of degree range
      const toLatLng = (x: number, y: number): [number, number] => {
        if (Math.abs(x) <= 180 && Math.abs(y) <= 90) return [y, x];
        const R = 6378137;
        return [(2 * Math.atan(Math.exp(y / R)) - Math.PI / 2) * (180 / Math.PI), (x / R) * (180 / Math.PI)];
      };

      // Color by coordinate_precision
      const precisionColor = (p?: string) => {
        if (p === "Approximate") return "#c47a00";
        if (p === "City") return "#6b7280";
        return "#9E1B1B"; // Exact (default)
      };

      // Spread co-located points so each pool is individually visible/tooltippable
      type SampleWithLatLng = typeof samples[0] & { lat: number; lng: number };
      const withCoords: SampleWithLatLng[] = samples
        .filter((s) => isFinite(s.x) && isFinite(s.y))
        .map((s) => { const [lat, lng] = toLatLng(s.x, s.y); return { ...s, lat, lng }; });
      const colocGroups = new Map<string, SampleWithLatLng[]>();
      for (const s of withCoords) {
        const key = `${s.lat.toFixed(5)},${s.lng.toFixed(5)}`;
        if (!colocGroups.has(key)) colocGroups.set(key, []);
        colocGroups.get(key)!.push(s);
      }
      const SPREAD_DEG = 0.00005;
      const spreadPoints: (SampleWithLatLng & { drawLat: number; drawLng: number })[] = [];
      Array.from(colocGroups.values()).forEach((group) => {
        if (group.length === 1) {
          spreadPoints.push({ ...group[0], drawLat: group[0].lat, drawLng: group[0].lng });
        } else {
          group.forEach((s: SampleWithLatLng, i: number) => {
            const angle = (2 * Math.PI * i) / group.length;
            spreadPoints.push({ ...s, drawLat: s.lat + SPREAD_DEG * Math.sin(angle), drawLng: s.lng + SPREAD_DEG * Math.cos(angle) });
          });
        }
      });

      approxMarkersRef.current = [];
      // Maps so a dragged point can drag its label + its mirror marker/label on the notice/export map
      const labelBySample = new Map<SampleWithLatLng, L.Marker>();
      const noticeMarkerBySample = new Map<SampleWithLatLng, L.Marker>();
      const noticeLabelBySample = new Map<SampleWithLatLng, L.Marker>();
      const markers = spreadPoints.map((s) => {
        const color = precisionColor(s.precision);
        const isDashed = s.precision === "Approximate" || s.precision === "City";
        const borderStyle = isDashed ? "dashed" : "solid";
        const m = L.marker([s.drawLat, s.drawLng], {
          draggable: false,
          icon: L.divIcon({
            className: "",
            html: `<div style="width:18px;height:18px;border-radius:50%;background:${color};border:3px ${borderStyle} #fff;box-shadow:0 0 0 1px ${color},0 2px 5px rgba(0,0,0,.3);"></div>`,
            iconSize: [18, 18],
            iconAnchor: [9, 9],
          }),
        }).bindTooltip(
          () => {
            const esc = (t: string) => t.replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c] as string));
            let html = esc(`${(s.diseases || ["WNV"]).join("/")}+ Pool ${s.pool ? "#" + s.pool : ""}${s.subgrid ? " - " + s.subgrid : ""}`);
            if (s.precision && s.precision !== "Exact") html += `<br><span style="color:#c47a00;font-weight:600;">${esc(s.precision)}</span>`;
            if (s.comments) html += `<br><span style="color:#555;font-style:italic;">${esc(s.comments)}</span>`;
            return html;
          },
          { direction: "top", offset: [0, -8] }
        );
        if (isDashed) {
          approxMarkersRef.current.push(m);
          // Keep the label + notice mirror (marker & label) glued to the point as it moves
          m.on("drag", () => {
            const pos = m.getLatLng();
            labelBySample.get(s)?.setLatLng(pos);
            noticeMarkerBySample.get(s)?.setLatLng(pos);
            noticeLabelBySample.get(s)?.setLatLng(pos);
          });
        }
        return m;
      });
      const group = L.layerGroup(markers);
      setPositiveCount(samples.length);
      positivesLayerRef.current = group;
      group.addTo(map);
      // Mirror (smaller, non-interactive) onto the notice layout — original coords (no spread needed at print scale)
      const noticeMarkers = spreadPoints.map((s) => {
        const color = precisionColor(s.precision);
        const nm2 = L.marker([s.lat, s.lng], {
          icon: L.divIcon({
            className: "",
            html: `<div style="width:12px;height:12px;border-radius:50%;background:${color};border:2px solid #fff;box-shadow:0 0 0 1px ${color};"></div>`,
            iconSize: [12, 12],
            iconAnchor: [6, 6],
          }),
          interactive: false,
        });
        noticeMarkerBySample.set(s, nm2);
        return nm2;
      });
      const noticeGroup = L.layerGroup(noticeMarkers);
      noticePositivesRef.current = noticeGroup;
      noticeGroup.addTo(nm);
      // Pool ID number labels (toggled separately, off by default)
      const mkLabel = (lat: number, lng: number, pool: string, size: number, offset: number) =>
        L.marker([lat, lng], {
          interactive: false,
          icon: L.divIcon({
            className: "",
            html: `<div style="font:700 ${size}px 'PT Sans',sans-serif;color:#9E1B1B;text-shadow:0 0 2px #fff,0 0 3px #fff,0 0 4px #fff;transform:translate(${offset}px,-${offset + 4}px);white-space:nowrap;">${pool}</div>`,
            iconSize: undefined as any,
            iconAnchor: [0, 0],
          }),
        });
      // Main map: register each label by sample so it can follow a dragged point
      const mainLabels: L.Marker[] = [];
      spreadPoints.filter((s) => s.pool).forEach((s) => {
        const lbl = mkLabel(s.drawLat, s.drawLng, s.pool!, 12, 10);
        labelBySample.set(s, lbl);
        mainLabels.push(lbl);
      });
      poolLabelsRef.current = L.layerGroup(mainLabels);
      // Notice map: labels at original coords, registered so they follow a dragged point
      const noticeLabels: L.Marker[] = [];
      spreadPoints.filter((s) => s.pool).forEach((s) => {
        const lbl = mkLabel(s.lat, s.lng, s.pool!, 11, 7);
        noticeLabelBySample.set(s, lbl);
        noticeLabels.push(lbl);
      });
      noticePoolLabelsRef.current = L.layerGroup(noticeLabels);
    } catch {}

    setTimeout(() => {
      map.invalidateSize();
      nm.invalidateSize();
    }, 400);

    return () => {
      map.remove();
      nm.remove();
      mapRef.current = null;
      noticeMapRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    for (const m of approxMarkersRef.current) {
      if (moveApprox) m.dragging?.enable();
      else m.dragging?.disable();
    }
  }, [moveApprox]);

  useEffect(() => {
    modeRef.current = mode;
    const map = mapRef.current;
    if (!map) return;
    map.pm.disableDraw();
    if (areaLayerRef.current) {
      areaLayerRef.current.pm.disable();
      renderNotice(); // leaving reshape finalizes the boundary on the notice
    }
    if (mode === "reshape" && areaLayerRef.current) {
      areaLayerRef.current.pm.enable({ snappable: true, snapDistance: 15 });
    }
    if (mode === "draw") {
      map.pm.enableDraw("Polygon", { snappable: true, snapDistance: 15 });
      map.once("pm:create", (e: any) => {
        if (areaLayerRef.current) map.removeLayer(areaLayerRef.current);
        areaLayerRef.current = e.layer;
        customGeomRef.current = true;
        e.layer.setStyle(AREA_STYLE);
        attachEditEvents(e.layer);
        try {
          setAcres(Math.round(turf.area(e.layer.toGeoJSON()) / 4046.856));
        } catch {}
        renderNotice();
        setMode("reshape");
      });
    }
  }, [mode, renderNotice]);

  const clearArea = () => {
    selectedRef.current.clear();
    setSelected([]);
    if (areaLayerRef.current && mapRef.current) {
      mapRef.current.removeLayer(areaLayerRef.current);
      areaLayerRef.current = null;
    }
    subgridLayerRef.current?.setStyle(cellStyle as any);
    setAcres(0);
    setEditingId(null);
    renderNotice();
  };

  // Road labels: draggable yellow tags on the notice mini-map (dblclick to edit)
  const addRoad = (text: string, at?: [number, number]) => {
    const nm = noticeMapRef.current;
    text = text.trim();
    if (!nm || !text) return;
    const b = nm.getBounds();
    const lat = at ? at[0] : b.getNorth() - (b.getNorth() - b.getSouth()) * (0.06 + roadsRef.current.length * 0.07);
    const lng = at ? at[1] : nm.getCenter().lng;
    const marker = L.marker([lat, lng], { draggable: true, icon: roadIcon(text) }).addTo(nm);
    const id = "r" + Date.now() + Math.round(Math.random() * 999);
    const rec: Road = { id, text, rotation: 0, marker, apply: () => {} };
    // Re-apply icon (rotation/text) and re-attach the scroll-to-rotate handler each time
    const applyIcon = () => {
      marker.setIcon(roadIcon(rec.text, rec.rotation));
      const el = marker.getElement();
      if (el) {
        el.addEventListener(
          "wheel",
          (e) => {
            e.preventDefault();
            e.stopPropagation();
            rec.rotation = (rec.rotation + (e.deltaY > 0 ? 5 : -5) + 360) % 360;
            applyIcon();
          },
          { passive: false }
        );
      }
    };
    rec.apply = applyIcon;
    applyIcon();
    roadsRef.current.push(rec);
    setRoadChips(roadsRef.current.map((r) => ({ id: r.id, text: r.text })));
  };

  // Auto-label the named roads along the treatment-area edges (OSM reverse geocode)
  const ABBR: [string, string][] = [
    ["East ", "E "], ["West ", "W "], ["North ", "N "], ["South ", "S "],
    ["Avenue", "Ave"], ["Street", "St"], ["Boulevard", "Blvd"], ["Drive", "Dr"], ["Road", "Rd"], ["Lane", "Ln"],
  ];
  const shortRoad = (n: string) => ABBR.reduce((s, [a, b]) => s.split(a).join(b), n);

  const autoLabelRoads = async () => {
    const area = areaLayerRef.current;
    if (!area) return;
    setBusy("Finding boundary roads…");
    // Clear existing labels first
    for (const r of roadsRef.current) noticeMapRef.current?.removeLayer(r.marker);
    roadsRef.current = [];
    setRoadChips([]);
    try {
      const b = area.getBounds();
      const probes: [number, number][] = [
        [b.getNorth(), b.getCenter().lng],
        [b.getSouth(), b.getCenter().lng],
        [b.getCenter().lat, b.getEast()],
        [b.getCenter().lat, b.getWest()],
      ];
      const seen = new Set<string>();
      for (const [lat, lng] of probes) {
        try {
          const res = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${lat}&lon=${lng}&zoom=16`,
            { headers: { Accept: "application/json" } }
          );
          const j = await res.json();
          const road = j?.address?.road;
          if (road) {
            const label = shortRoad(road);
            if (!seen.has(label.toLowerCase())) {
              seen.add(label.toLowerCase());
              addRoad(label, [lat, lng]);
            }
          }
        } catch {}
      }
    } finally {
      setBusy("");
    }
  };

  const rotateRoad = (id: string, delta: number) => {
    const rec = roadsRef.current.find((r) => r.id === id);
    if (!rec) return;
    rec.rotation = (rec.rotation + delta + 360) % 360;
    rec.apply();
  };

  const renameRoad = (id: string, text: string) => {
    const rec = roadsRef.current.find((r) => r.id === id);
    if (!rec) return;
    rec.text = text;
    rec.apply();
    setRoadChips(roadsRef.current.map((r) => ({ id: r.id, text: r.text })));
  };

  const removeRoad = (id: string) => {
    const i = roadsRef.current.findIndex((r) => r.id === id);
    if (i >= 0) {
      noticeMapRef.current?.removeLayer(roadsRef.current[i].marker);
      roadsRef.current.splice(i, 1);
      setRoadChips(roadsRef.current.map((r) => ({ id: r.id, text: r.text })));
    }
  };

  // Snapshot / restore the notice's appearance alongside the polygon (see NoticeLayout)
  const captureNoticeLayout = (): NoticeLayout => {
    const nm = noticeMapRef.current;
    const c = nm?.getCenter();
    return {
      roads: roadsRef.current.map((r) => {
        const ll = r.marker.getLatLng();
        return { text: r.text, lat: ll.lat, lng: ll.lng, rotation: r.rotation };
      }),
      customBody,
      basemap,
      mapTemplate,
      center: c ? [c.lat, c.lng] : null,
      zoom: nm?.getZoom() ?? null,
    };
  };

  const clearRoads = () => {
    for (const r of roadsRef.current) noticeMapRef.current?.removeLayer(r.marker);
    roadsRef.current = [];
    setRoadChips([]);
  };

  const restoreNoticeLayout = (n?: NoticeLayout) => {
    clearRoads();
    if (!n) return;
    setCustomBody(n.customBody ?? null);
    if (n.mapTemplate) setMapTemplate(n.mapTemplate);
    n.roads?.forEach((r) => {
      addRoad(r.text, [r.lat, r.lng]);
      const rec = roadsRef.current[roadsRef.current.length - 1];
      if (rec && r.rotation) {
        rec.rotation = r.rotation;
        rec.apply();
      }
    });
  };

  /** Save the area locally first (so a network failure can never lose the work), then
   *  mirror it to the AGOL treatment layer as Status='Draft' — durable and cross-machine,
   *  but invisible to the public site, which filters on Status='Upcoming'. */
  const finalize = async () => {
    renderNotice(); // make sure the preview reflects the final boundary
    const geom = areaLayerRef.current ? (areaLayerRef.current.toGeoJSON().geometry as any) : null;
    if (!geom) {
      alert("No treatment area on the map yet.");
      return;
    }
    const id = editingId || `area_${Date.now()}`;
    const prior = savedAreas.find((a) => a.id === id);
    // What the boundary covers, not what was clicked — a straddling area must record both
    // halves so mark-treated doesn't leave treated ground marked untreated.
    const labels = coveredGridlabels(geom);
    const area: TreatmentArea = {
      id,
      geometry: geom,
      gridlabels: labels.length ? labels : Array.from(selectedRef.current).sort(),
      date,
      timeWindow,
      reason,
      acres,
      savedAt: new Date().toISOString(),
      notice: captureNoticeLayout(),
      agolObjectId: prior?.agolObjectId ?? null,
      agolStatus: prior?.agolStatus ?? null,
      syncError: null,
    };
    setSavedAreas(upsertTreatmentArea(area));
    // Stay attached to the area just saved: the polygon is still on the map, so a second
    // click must update it rather than add a duplicate (and its own AGOL draft), and the
    // review overlay must not draw a violet copy on top of the active blue one.
    setEditingId(id);

    // Editing an already-scheduled area must not pull it back off the public map: keep it
    // 'Upcoming' and update in place. Only genuinely new/unscheduled areas are drafts.
    const nextStatus = prior?.agolStatus === "Upcoming" ? "Upcoming" : "Draft";

    setBusy(nextStatus === "Upcoming" ? "Updating scheduled area…" : "Saving area to AGOL…");
    try {
      const res = await api.post("/publish-treatment-area", {
        layer_id: getTreatmentLayerId(),
        geometry: geom,
        treatment_date: isoToMdy(date),
        treatment_time: timeWindow,
        reason,
        gridlabels: area.gridlabels,
        area_status: nextStatus,
        object_id: area.agolObjectId ?? undefined,
      });
      setSavedAreas(
        upsertTreatmentArea({ ...area, agolObjectId: res.data.object_id ?? area.agolObjectId, agolStatus: nextStatus, syncError: null })
      );
    } catch (e: any) {
      // Keep the local copy and flag it — the area is safe, just not mirrored yet
      const msg = e?.response?.data?.message || e.message || "unknown error";
      setSavedAreas(upsertTreatmentArea({ ...area, syncError: msg }));
    } finally {
      setBusy("");
    }
  };

  // Load a saved area back onto the map for review/editing
  const loadArea = (a: TreatmentArea) => {
    const map = mapRef.current;
    if (!map || !a.geometry) return;
    if (areaLayerRef.current) map.removeLayer(areaLayerRef.current);
    const layer = L.geoJSON(a.geometry as any, { style: AREA_STYLE });
    const poly = layer.getLayers()[0] as L.Polygon;
    poly.addTo(map);
    areaLayerRef.current = poly;
    attachEditEvents(poly);
    customGeomRef.current = true; // treat as free-form; cells stay un-highlighted
    selectedRef.current = new Set(a.gridlabels);
    setSelected([...a.gridlabels]);
    subgridLayerRef.current?.setStyle(cellStyle as any);
    setAcres(a.acres);
    setDate(a.date);
    setTimeWindow(a.timeWindow);
    setReason(a.reason);
    setEditingId(a.id);
    try {
      map.fitBounds(poly.getBounds().pad(0.3));
    } catch {}
    renderNotice();
    // Bring back this area's own road labels / footer / notice view
    restoreNoticeLayout(a.notice);
    const nm = noticeMapRef.current;
    if (nm && a.notice?.center && a.notice.zoom != null) {
      // after renderNotice's fitBounds, so a manual pan/zoom wins
      setTimeout(() => nm.setView(a.notice!.center as any, a.notice!.zoom as any, { animate: false }), 40);
    }
  };

  const removeArea = async (id: string) => {
    const area = savedAreas.find((a) => a.id === id);
    setSavedAreas(deleteTreatmentArea(id));
    if (editingId === id) setEditingId(null);
    // Drop the mirrored draft too, so the layer doesn't keep an area you discarded.
    // Scheduled areas are left alone — clearing those is the end-of-week step.
    if (area?.agolObjectId != null && area.agolStatus === "Draft") {
      try {
        await api.post("/delete-treatment-area", { layer_id: getTreatmentLayerId(), object_id: area.agolObjectId });
      } catch (e: any) {
        alert(`Removed locally, but the AGOL draft could not be deleted: ${e?.response?.data?.message || e.message}`);
      }
    }
  };

  /** Call off a scheduled area: flip it back to 'Draft' so it drops off the public website
   *  and the webmap immediately, while staying here as a saved area that can be edited and
   *  re-scheduled. Deliberately not 'Completed' — that would claim the area was treated.
   *
   *  Uses the publish upsert (status + object_id) rather than a dedicated route, so the
   *  geometry and attributes are rewritten from the local copy unchanged. */
  const unscheduleArea = async (id: string) => {
    const area = savedAreas.find((a) => a.id === id);
    if (!area?.agolObjectId || area.agolStatus !== "Upcoming" || !area.geometry) return;
    if (
      !window.confirm(
        `Call off ${areaLabel(area)}?\n\n` +
          `It will drop off the public website and the adulticide webmap right away, and stay here ` +
          `as a draft you can re-schedule. It will NOT be recorded as treated.`
      )
    )
      return;

    setBusy("Calling off area…");
    try {
      await api.post("/publish-treatment-area", {
        layer_id: getTreatmentLayerId(),
        geometry: area.geometry,
        treatment_date: isoToMdy(area.date),
        treatment_time: area.timeWindow,
        reason: area.reason,
        gridlabels: area.gridlabels,
        area_status: "Draft",
        object_id: area.agolObjectId,
      });
      await syncFromAgol();
    } catch (e: any) {
      alert(`Could not call off the area: ${e?.response?.data?.message || e.message}`);
    } finally {
      setBusy("");
    }
  };

  /** Flip every mirrored draft to Status='Upcoming'. This is the step that replaces the
   *  ArcGIS Pro round-trip — the layer is already on the adulticide webmap. */
  const scheduleForTreatment = async () => {
    // Refresh status from AGOL first: a chip left stale by a change made elsewhere would
    // otherwise be "scheduled" here, match nothing on the layer, and report 0 as success.
    setBusy("Checking AGOL…");
    await syncFromAgol();
    setBusy("");
    const current = loadTreatmentAreas();

    const drafts = current.filter((a) => a.agolObjectId != null && a.agolStatus === "Draft");
    if (!drafts.length) {
      const already = current.filter((a) => a.agolStatus === "Upcoming").length;
      const unsynced = current.filter((a) => a.agolObjectId == null).length;
      if (already && !unsynced) {
        alert(`Nothing to schedule — all ${already} area${already === 1 ? " is" : "s are"} already scheduled.`);
      } else if (unsynced) {
        alert(`No areas are mirrored to AGOL yet (${unsynced} local-only). Re-save them while logged in first.`);
      } else {
        alert("No draft areas to schedule.");
      }
      return;
    }
    if (
      !window.confirm(
        `Schedule ${drafts.length} treatment area${drafts.length > 1 ? "s" : ""} for treatment?\n\n` +
          `They will flip to "Upcoming" on the Adulticide Treatment Areas layer, which makes them ` +
          `visible on the adulticide webmap and the public website.`
      )
    )
      return;

    setBusy("Scheduling…");
    try {
      const res = await api.post("/schedule-treatment-areas", {
        layer_id: getTreatmentLayerId(),
        object_ids: drafts.map((a) => a.agolObjectId),
      });
      // Re-read status from the layer rather than assuming the request took effect
      await syncFromAgol();
      const n = res.data.scheduled ?? 0;
      if (n === 0) {
        alert(
          `Nothing was scheduled — none of the ${drafts.length} area${drafts.length === 1 ? "" : "s"} ` +
            `were still drafts on AGOL. Statuses have been refreshed; check the chips.`
        );
      } else {
        alert(`Scheduled ${n} area${n === 1 ? "" : "s"} for treatment.`);
      }
    } catch (e: any) {
      alert(`Scheduling failed: ${e?.response?.data?.message || e.message}`);
    } finally {
      setBusy("");
    }
  };

  /** Reconcile the local mirror against the layer.
   *
   *  AGOL is the authority for Status: an area can be scheduled from another machine, or
   *  edited on AGOL directly, and the browser would otherwise keep showing a stale chip
   *  forever (and then "schedule" it, matching nothing). Local records stay authoritative for
   *  the notice layout, which AGOL has nowhere to store — so they are updated, never replaced.
   *  Areas on the layer we've never seen are adopted, so a cleared localStorage or a different
   *  machine still recovers the run. */
  useEffect(() => {
    editingIdRef.current = editingId;
  }, [editingId]);

  const syncFromAgol = useCallback(async (): Promise<string | null> => {
    let remote: any[];
    try {
      const res = await api.post("/list-treatment-areas", { layer_id: getTreatmentLayerId() });
      remote = res.data?.areas || [];
      setSyncWarning("");
    } catch {
      // Local copies still work, but the statuses shown may no longer match the layer —
      // say so instead of leaving a chip reading "scheduled" for a cleared area.
      setSyncWarning("Can't reach AGOL — area statuses may be out of date");
      return null;
    }
    const byOid = new Map<number, any>(remote.map((r) => [r.object_id, r]));
    let restatused = 0;
    let reshaped = 0;
    let orphaned = 0;
    let closed = 0;

    loadTreatmentAreas().forEach((a) => {
      if (a.agolObjectId == null) return;
      const r = byOid.get(a.agolObjectId);
      if (!r) {
        // Its feature is gone from the layer — forget the link so a re-save creates a new one
        upsertTreatmentArea({ ...a, agolObjectId: null, agolStatus: null });
        orphaned++;
      } else if (r.status === "Completed") {
        // The week is closed out. Drop the local record so next week's list starts clean —
        // the polygon stays on the layer as history, and the treated subgrids carry
        // DateTreated. Only the notice layout is lost, and its notices are already exported.
        deleteTreatmentArea(a.id);
        closed++;
      } else {
        // Take the shape back from AGOL when it differs. Without this a draft
        // edited by someone else stays whatever this browser last drew, and two
        // people editing the same feature quietly see different polygons.
        //
        // Only geometry, grid labels and acreage are adopted. The notice layout
        // is local-only -- AGOL has nowhere to store it -- so it survives, but
        // label positions were placed against the old outline and will be off
        // after a reshape. That is the accepted trade: a correct shape matters
        // more than label placement, which is re-done before export anyway.
        // Never pull a shape out from under an in-progress edit: this runs on
        // tab focus, so switching away and back mid-reshape would otherwise
        // discard the work. Status still refreshes.
        const beingEdited = a.id === editingIdRef.current;
        const geomChanged =
          !beingEdited &&
          r.geometry &&
          JSON.stringify(r.geometry) !== JSON.stringify(a.geometry);
        const labelsChanged =
          !beingEdited &&
          Array.isArray(r.gridlabels) &&
          r.gridlabels.join(",") !== (a.gridlabels || []).join(",");
        const statusChanged = Boolean(r.status && r.status !== a.agolStatus);

        if (geomChanged || labelsChanged || statusChanged) {
          let acres = a.acres;
          if (geomChanged) {
            try {
              acres = Math.round(turf.area(r.geometry as any) / 4046.856);
            } catch {
              /* keep the previous figure rather than showing nothing */
            }
          }
          upsertTreatmentArea({
            ...a,
            agolStatus: r.status || a.agolStatus,
            geometry: geomChanged ? r.geometry : a.geometry,
            gridlabels: labelsChanged ? r.gridlabels : a.gridlabels,
            acres,
          });
          if (geomChanged || labelsChanged) reshaped++;
          else restatused++;
        }
      }
    });

    const known = new Set(loadTreatmentAreas().map((a) => a.agolObjectId).filter((o) => o != null));
    let adopted = 0;
    remote.forEach((r) => {
      if (known.has(r.object_id)) return;
      // Only adopt the run in progress. 'Completed' areas are history — pulling them in would
      // make every past week pile up in the saved-areas list.
      if (r.status !== "Draft" && r.status !== "Upcoming") return;
      let ac = 0;
      try {
        ac = Math.round(turf.area(r.geometry as any) / 4046.856);
      } catch {}
      upsertTreatmentArea({
        id: `agol_${r.object_id}`,
        geometry: r.geometry,
        gridlabels: r.gridlabels || [],
        date: mdyToIso(r.treatment_date) || new Date().toISOString().slice(0, 10),
        timeWindow: r.treatment_time || "",
        reason: r.disease === "WNV" ? "wnv" : "abundance",
        acres: ac,
        savedAt: new Date().toISOString(),
        agolObjectId: r.object_id,
        agolStatus: r.status || "Draft",
      });
      adopted++;
    });

    setSavedAreas(loadTreatmentAreas());
    const bits: string[] = [];
    if (adopted) bits.push(`${adopted} recovered from AGOL`);
    if (reshaped) bits.push(`${reshaped} updated from AGOL`);
    if (restatused) bits.push(`${restatused} status refreshed`);
    if (closed) bits.push(`${closed} completed area(s) cleared from the list`);
    if (orphaned) bits.push(`${orphaned} no longer on AGOL`);
    return bits.length ? bits.join(" · ") : null;
  }, []);

  useEffect(() => {
    let cancelled = false;
    const refresh = async () => {
      const summary = await syncFromAgol();
      if (!cancelled && summary) setLoadStatus(summary);
    };
    refresh();
    // Status can change outside this page — Clear Treatment Schedule runs from the run/tools
    // pages, and another machine may schedule. Without this, a builder tab left open keeps
    // showing "scheduled" for areas that were cleared long ago.
    const onWake = () => {
      if (document.visibilityState === "visible") refresh();
    };
    document.addEventListener("visibilitychange", onWake);
    window.addEventListener("focus", onWake);
    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", onWake);
      window.removeEventListener("focus", onWake);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** Ask Esri whether the current vector basemap style will actually load, and surface the
   *  reason if it won't. A rejected or expired API key otherwise renders a blank map with no
   *  explanation — which is how a dead key went unnoticed in production once. Distinguishes a
   *  refused key from an unreachable service so the fix is obvious. */
  useEffect(() => {
    const bm = BASEMAPS[basemap];
    if (bm.kind !== "vector") {
      setBasemapIssue("");
      return;
    }
    if (!ARCGIS_API_KEY) {
      setBasemapIssue("No ArcGIS API key set — showing raster tiles");
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(
          `https://basemapstyles-api.arcgis.com/arcgis/rest/services/styles/v2/styles/${bm.style}` +
            `?token=${encodeURIComponent(ARCGIS_API_KEY)}`
        );
        if (cancelled) return;
        if (res.ok) {
          setBasemapIssue("");
          return;
        }
        let detail = `HTTP ${res.status}`;
        try {
          const body = await res.json();
          if (body?.error?.message) detail = body.error.message;
        } catch {}
        setBasemapIssue(
          res.status === 401 || res.status === 403
            ? `Basemap blocked — ArcGIS key rejected (${detail})`
            : `Basemap unavailable — ${detail}`
        );
      } catch {
        if (!cancelled) setBasemapIssue("Basemap unavailable — can't reach Esri");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [basemap]);

  // Review overlay: draw every saved area at once so the whole week can be checked before
  // scheduling. The area under edit is skipped — areaLayerRef already draws it in AREA_STYLE.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (allAreasLayerRef.current) {
      map.removeLayer(allAreasLayerRef.current);
      allAreasLayerRef.current = null;
    }
    if (!showAllAreas) return;

    const group = L.layerGroup();
    savedAreas.forEach((a) => {
      if (!a.geometry || a.id === editingId) return;
      L.geoJSON(a.geometry as any, { style: SAVED_STYLE })
        .bindTooltip(`${areaLabel(a)} · ${a.acres} ac · ${a.date}`, { direction: "center", className: "gridlabel", opacity: 1 })
        .on("click", () => loadArea(a))
        .addTo(group);
    });
    group.addTo(map);
    allAreasLayerRef.current = group;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showAllAreas, savedAreas, editingId]);

  // Zoom out to cover every saved area (plus the active one) in one view
  const fitAllAreas = () => {
    const map = mapRef.current;
    if (!map) return;
    let bounds: L.LatLngBounds | null = null;
    const extend = (b: L.LatLngBounds) => { bounds = bounds ? bounds.extend(b) : b; };
    savedAreas.forEach((a) => {
      if (!a.geometry) return;
      try { extend(L.geoJSON(a.geometry as any).getBounds()); } catch {}
    });
    if (areaLayerRef.current) {
      try { extend(areaLayerRef.current.getBounds()); } catch {}
    }
    if (bounds) {
      try { map.fitBounds((bounds as L.LatLngBounds).pad(0.15)); } catch {}
    }
  };

  // Reach the maplibre map behind the notice vector basemap (null for raster basemaps)
  const getNoticeGlMap = (): any => {
    const layer: any = noticeBaseTileRef.current;
    try { return layer?._maplibreGL?.getMaplibreMap?.() ?? layer?._glMap ?? null; } catch { return null; }
  };

  const NOTICE_EXPORT_RATIO = 4;
  const sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

  const downloadDataUrl = (dataUrl: string, filename: string) => {
    const a = document.createElement("a");
    a.href = dataUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  /** Name a notice after every subgrid it covers, not just the first — a straddling area's
   *  file would otherwise claim one subgrid while the notice inside names two. */
  const noticeName = (gridlabels: string[] | undefined, isoDate: string) => {
    const clean = (gridlabels || []).map((g) => String(g).replace(/[^0-9A-Za-z-]/g, "")).filter(Boolean);
    return `CommunityNotice_${clean.length ? clean.join("_") : "area"}_${stampFor(isoDate)}`;
  };

  /** Render the notice's vector basemap at export resolution for the duration of `fn`,
   *  then restore it. Batch exports boost once around the whole loop rather than per notice. */
  const withNoticeGlBoost = async <T,>(fn: () => Promise<T>): Promise<T> => {
    const glMap = getNoticeGlMap();
    const prevPixelRatio = glMap?.getPixelRatio?.() ?? (window.devicePixelRatio || 1);
    try {
      if (glMap?.setPixelRatio) {
        glMap.setPixelRatio(NOTICE_EXPORT_RATIO);
        await new Promise<void>((resolve) => {
          let done = false;
          const finish = () => { if (!done) { done = true; resolve(); } };
          glMap.once("idle", finish);
          setTimeout(finish, 1500);
        });
      }
      return await fn();
    } finally {
      if (glMap?.setPixelRatio) glMap.setPixelRatio(prevPixelRatio);
    }
  };

  /** Capture just the notice, then composite onto a white canvas with exact, even margins
   *  on all four sides (EXPORT_MARGIN_PX) — no CSS measurement quirks. */
  const captureNoticePng = async () => {
    const ratio = NOTICE_EXPORT_RATIO;
    const inner = await toPng(noticeRef.current!, { cacheBust: true, pixelRatio: ratio, backgroundColor: "#ffffff" });
    const img = new Image();
    await new Promise((res, rej) => {
      img.onload = res;
      img.onerror = rej;
      img.src = inner;
    });
    const m = EXPORT_MARGIN_PX * ratio;
    const canvas = document.createElement("canvas");
    canvas.width = img.width + 2 * m;
    canvas.height = img.height + 2 * m;
    const ctx = canvas.getContext("2d")!;
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(img, m, m);
    return { png: canvas.toDataURL("image/png"), width: canvas.width, height: canvas.height };
  };

  /** Wait for React to commit the newly-loaded area and for the notice map to finish drawing.
   *  The leading sleep clears renderNotice's fitBounds/invalidateSize and loadArea's setView timers. */
  const settleNotice = async () => {
    await sleep(260);
    const glMap = getNoticeGlMap();
    const rasterTiles: any = glMap ? null : noticeBaseTileRef.current; // raster fallback basemap
    await new Promise<void>((resolve) => {
      let done = false;
      const finish = () => { if (!done) { done = true; resolve(); } };
      if (glMap?.once) glMap.once("idle", finish);
      else if (rasterTiles?.once) rasterTiles.once("load", finish);
      setTimeout(finish, 1800);
    });
  };

  const exportNotice = async (kind: "png" | "pdf") => {
    if (!noticeRef.current) return;
    setBusy(`Exporting ${kind.toUpperCase()}…`);
    try {
      const { png, width, height } = await withNoticeGlBoost(captureNoticePng);
      const name = noticeName(noticeLabels, date);
      if (kind === "png") {
        downloadDataUrl(png, `${name}.png`);
      } else {
        // Fit the composited image (margins included) centered on the letter page
        const pdf = new jsPDF({ unit: "in", format: "letter", orientation: "portrait" });
        const aspect = height / width;
        let w = 8.1;
        let h = w * aspect;
        if (h > 10.6) {
          h = 10.6;
          w = h / aspect;
        }
        pdf.addImage(png, "PNG", (8.5 - w) / 2, (11 - h) / 2, w, h);
        pdf.save(`${name}.pdf`);
      }
    } catch (e: any) {
      alert(`Export failed (map tiles may still be loading): ${e.message}`);
    } finally {
      setBusy("");
    }
  };

  /** Export one PNG per saved area in a single action — replaces doing Export PNG 7-12
   *  times by hand. Each area's own notice layout is restored before it's captured, so the
   *  road labels and footer match that area rather than whatever was last on screen. */
  const exportAllNotices = async () => {
    const areas = savedAreas.filter((a) => a.geometry);
    if (!areas.length) {
      alert("No saved areas yet. Save at least one area for the run first.");
      return;
    }
    const resumeId = editingId;
    try {
      await withNoticeGlBoost(async () => {
        for (let i = 0; i < areas.length; i++) {
          const a = areas[i];
          setBusy(`Exporting notice ${i + 1}/${areas.length}…`);
          loadArea(a);
          await settleNotice();
          const { png } = await captureNoticePng();
          downloadDataUrl(png, `${noticeName(a.gridlabels, a.date)}.png`);
          await sleep(350); // stagger so the browser doesn't drop queued downloads
        }
      });
    } catch (e: any) {
      alert(`Batch export stopped: ${e.message}`);
    } finally {
      setBusy("");
      // Put the builder back on whatever was being edited before the batch
      const resume = areas.find((a) => a.id === resumeId);
      if (resume) loadArea(resume);
    }
  };

  // MMDDYYYY filename stamp from a YYYY-MM-DD date string
  const stampFor = (isoDate: string) => {
    const d = new Date(isoDate + "T12:00:00");
    return `${String(d.getMonth() + 1).padStart(2, "0")}${String(d.getDate()).padStart(2, "0")}${d.getFullYear()}`;
  };

  // Zipped shapefile of either the area on the map now, or every saved area for the run.
  // The backend splits MultiPolygons into one record each and reads per-feature date/time.
  const downloadShapefile = async (scope: "active" | "all" = "active") => {
    const features: any[] = [];
    if (scope === "all") {
      savedAreas.forEach((a) => {
        if (a.geometry) features.push({ type: "Feature", properties: { date: a.date, time: a.timeWindow }, geometry: a.geometry });
      });
      if (!features.length) {
        alert("No saved areas yet. Save at least one area for the run first.");
        return;
      }
    } else {
      const cur = areaLayerRef.current?.toGeoJSON() as any;
      if (!cur?.geometry) {
        alert("No treatment area on the map to export. Pick subgrids or draw an area first.");
        return;
      }
      features.push({ type: "Feature", properties: { date, time: timeWindow }, geometry: cur.geometry });
    }
    // Name the zip after the earliest treatment date it covers
    const stamp = stampFor(
      scope === "all" ? savedAreas.map((a) => a.date).filter(Boolean).sort()[0] || date : date
    );
    setBusy("Building shapefile…");
    try {
      const res = await api.post("/export-treatment-areas-shapefile", { features, stamp }, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Treatment_Areas_${stamp}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e: any) {
      const msg = e?.response?.data?.message || e.message;
      alert(`Shapefile export failed: ${msg}`);
    } finally {
      setBusy("");
    }
  };

  const fmtDate = new Date(date + "T12:00:00").toLocaleDateString("en-US", {
    weekday: "long", month: "short", day: "numeric", year: "numeric",
  });
  const [winStart, winEnd] = timeWindow.split(" - ");
  const black = "#111";
  const draftCount = savedAreas.filter((a) => a.agolObjectId != null && a.agolStatus === "Draft").length;
  // Notices name every subgrid the area actually covers; fall back to the picked cells
  // before any boundary exists (or if coverage can't be computed).
  const noticeLabels = covered.length ? covered : selected;

  return (
    <div className="h-screen flex flex-col bg-paper">
      <header className="bg-card border-b border-line px-4 py-2.5 flex items-center gap-3 shrink-0">
        <Link to="/workflows/adult-control" title="Back to Run"><img src="/logo512.png" alt="CMAD" className="w-[34px] h-[34px]" /></Link>
        <div className="font-extrabold text-[15px] tracking-tight">Pioneer <span className="text-ink-faint font-medium">· Adult Control Run</span></div>
        <div className="hidden min-[860px]:flex items-center gap-2 ml-1.5 text-[12.5px] font-semibold">
          {[["✓", "Detect", "done"], ["2", "Build Treatment Area", "active"], ["3", "Notice", ""], ["4", "Notify", ""]].map(([dot, label, st], i) => (
            <React.Fragment key={label}>
              {i > 0 && <span className="text-line-strong">›</span>}
              <span className={`flex items-center gap-1.5 ${st === "active" ? "text-brand-deep" : st === "done" ? "text-ink-soft" : "text-ink-faint"}`}>
                <span className={`w-[18px] h-[18px] rounded-full flex items-center justify-center text-[10.5px] border ${
                  st === "active" ? "bg-brand border-brand text-white" : st === "done" ? "bg-brand-tint border-[#bcd6a8] text-brand-deep" : "border-line-strong"
                }`}>{dot}</span>
                {label}
              </span>
            </React.Fragment>
          ))}
        </div>
        <div className="ml-auto flex items-center gap-2.5">
          {syncWarning && (
            <span
              className="pill bg-wnv-tint text-wnv font-bold"
              title="The treatment layer could not be re-read, so the scheduled/draft/completed labels below may be stale. Reload once you're logged in."
            >
              ⚠ {syncWarning}
            </span>
          )}
          {basemapIssue && (
            <span
              className="pill bg-wnv-tint text-wnv font-bold"
              title="The Esri vector basemap is not loading. Notices exported now would have a blank map."
            >
              ⚠ {basemapIssue}
            </span>
          )}
          {busy && <span className="pill bg-info-tint text-info">{busy}</span>}
          <span className="flex items-center gap-2 rounded-full px-3 py-1 text-[12.5px] font-semibold border" style={{ background: "#fbeeec", borderColor: "#e6c4bd", color: "#9E1B1B" }}>
            <span className="w-[9px] h-[9px] rounded-full bg-wnv" />
            {selected.length ? <>WNV+ · <span className="font-mono">{selected[0]}</span></> : loadStatus}
          </span>
        </div>
      </header>

      <div className="flex-1 flex min-h-0 flex-col min-[1000px]:flex-row">
        {/* Map canvas */}
        <div className="flex-1 relative min-h-[320px]">
          <div ref={mapDivRef} className="absolute inset-0" />
          {/* Floating tool panel (prototype style) */}
          <div className="absolute top-3.5 left-3.5 z-[1000] bg-card border border-line rounded-[12px] p-2 w-[208px]" style={{ boxShadow: "0 4px 16px rgba(24,33,28,0.10)" }}>
            <div className="eyebrow px-1 pb-2">Treatment Area</div>
            {([
              ["pick", "Pick subgrid", "Click cells that contain the positive"],
              ["reshape", "Reshape boundary", "Drag vertices · snaps to subgrids"],
              ["draw", "Draw custom", "Free polygon with snapping"],
            ] as [Mode, string, string][]).map(([m, label, desc]) => (
              <button
                key={m}
                onClick={() => setMode(m)}
                className={`w-full text-left rounded-lg px-2.5 py-2 text-[13px] font-semibold border transition ${
                  mode === m ? "bg-brand-tint border-[#bcd6a8] text-brand-deep" : "border-transparent hover:bg-paper text-ink"
                }`}
              >
                {label}
                <small className="block font-normal text-ink-faint text-[11px] mt-px">{desc}</small>
              </button>
            ))}
            <div className="h-px bg-line my-1.5 mx-0.5" />
            <label className="flex items-center gap-2 px-2.5 py-1.5 text-[12.5px] font-semibold cursor-pointer select-none">
              <input
                type="checkbox"
                checked={showPositives}
                onChange={(e) => {
                  setShowPositives(e.target.checked);
                  const g = positivesLayerRef.current;
                  const m = mapRef.current;
                  const nm = noticeMapRef.current;
                  const ng = noticePositivesRef.current;
                  if (g && m) (e.target.checked ? g.addTo(m) : m.removeLayer(g));
                  if (ng && nm) (e.target.checked ? ng.addTo(nm) : nm.removeLayer(ng));
                  if (!e.target.checked) {
                    // labels can't outlive the points
                    setShowPoolLabels(false);
                    if (poolLabelsRef.current && m) m.removeLayer(poolLabelsRef.current);
                    if (noticePoolLabelsRef.current && nm) nm.removeLayer(noticePoolLabelsRef.current);
                  }
                }}
                className="w-3.5 h-3.5 accent-[#9E1B1B]"
              />
              <span className="w-2.5 h-2.5 rounded-full bg-wnv border border-white shadow" />
              {/* Not a date-filtered count — these are whatever Step 2 last analyzed in THIS
                  browser, so say that rather than implying a week window that doesn't exist. */}
              Disease positives {positiveCount ? `(${positiveCount})` : "(run Step 2 first)"}
            </label>
            <label className="flex items-center gap-2 pl-7 pr-2.5 py-1 text-[12px] font-semibold cursor-pointer select-none text-ink-soft">
              <input
                type="checkbox"
                checked={showPoolLabels}
                disabled={!showPositives || !positiveCount}
                onChange={(e) => {
                  setShowPoolLabels(e.target.checked);
                  const m = mapRef.current;
                  const nm = noticeMapRef.current;
                  const g = poolLabelsRef.current;
                  const ng = noticePoolLabelsRef.current;
                  if (g && m) (e.target.checked ? g.addTo(m) : m.removeLayer(g));
                  if (ng && nm) (e.target.checked ? ng.addTo(nm) : nm.removeLayer(ng));
                }}
                className="w-3.5 h-3.5 accent-[#9E1B1B]"
              />
              Pool ID labels
            </label>
            <label className={`flex items-center gap-2 pl-7 pr-2.5 py-1 text-[12px] font-semibold cursor-pointer select-none ${moveApprox ? "text-[#c47a00]" : "text-ink-soft"}`}>
              <input
                type="checkbox"
                checked={moveApprox}
                disabled={!showPositives || !positiveCount}
                onChange={(e) => setMoveApprox(e.target.checked)}
                className="w-3.5 h-3.5"
                style={{ accentColor: "#c47a00" }}
              />
              Move approx. points
            </label>
            {showPositives && positiveCount > 0 && (
              <div className="pl-7 pr-2.5 pb-1 flex flex-col gap-0.5">
                {/* Exact / Approximate only. 'City' precision isn't used in practice; such a
                    point would still draw grey-dashed and name its precision in the tooltip. */}
                {([["#9E1B1B", "solid", "Exact"], ["#c47a00", "dashed", "Approximate"]] as [string, string, string][]).map(([color, border, label]) => (
                  <span key={label} className="flex items-center gap-1.5 text-[10.5px] text-ink-faint">
                    <span style={{ width: 10, height: 10, borderRadius: "50%", background: color, border: `2px ${border} white`, boxShadow: `0 0 0 1px ${color}`, flexShrink: 0 }} />
                    {label}
                  </span>
                ))}
              </div>
            )}
            <div className="h-px bg-line my-1.5 mx-0.5" />
            <div className="eyebrow px-1 pb-1">Basemap</div>
            <div className="flex gap-1 px-1 pb-1">
              {(Object.keys(BASEMAPS) as BasemapKey[]).map((key) => (
                <button
                  key={key}
                  onClick={() => {
                    if (key === basemap) return;
                    const map = mapRef.current;
                    if (map && baseTileRef.current) {
                      map.removeLayer(baseTileRef.current);
                      baseTileRef.current = makeBaseTile(key).addTo(map);
                      sendBack(baseTileRef.current);
                    }
                    const nm = noticeMapRef.current;
                    if (nm && noticeBaseTileRef.current) {
                      nm.removeLayer(noticeBaseTileRef.current);
                      noticeBaseTileRef.current = makeNoticeBase(key).addTo(nm);
                      sendBack(noticeBaseTileRef.current);
                      setTimeout(() => nm.invalidateSize(), 30);
                    }
                    setBasemap(key);
                  }}
                  className={`flex-1 py-1 rounded-md text-[11.5px] font-semibold border transition ${
                    basemap === key ? "bg-brand-tint border-[#bcd6a8] text-brand-deep" : "border-line text-ink-soft hover:bg-paper"
                  }`}
                >
                  {BASEMAPS[key].label}
                </button>
              ))}
            </div>
            <div className="h-px bg-line my-1.5 mx-0.5" />
            <div className="eyebrow px-1 pb-1">AGOL layers</div>
            <label className="flex items-center gap-2 px-2.5 py-1 text-[12.5px] font-semibold cursor-pointer select-none">
              <input
                type="checkbox"
                checked={showSubgrids}
                onChange={(e) => {
                  setShowSubgrids(e.target.checked);
                  const g = subgridLayerRef.current;
                  const m = mapRef.current;
                  if (g && m) (e.target.checked ? g.addTo(m) : m.removeLayer(g));
                }}
                className="w-3.5 h-3.5 accent-[#5B9E3F]"
              />
              <span className="truncate">Subgrids</span>
            </label>
            {overlays.map((o) => (
              <label key={o.layerId} className="flex items-center gap-2 px-2.5 py-1 text-[12.5px] font-semibold cursor-pointer select-none">
                <input type="checkbox" checked={o.on} disabled={o.loading} onChange={(e) => toggleOverlay(o.layerId, e.target.checked)} className="w-3.5 h-3.5 accent-[#5B9E3F]" />
                <span className="truncate">{o.name}</span>
                {o.loading && <span className="text-ink-faint text-[10px] ml-auto">…</span>}
                {o.error && <span className="text-wnv text-[10px] ml-auto" title={o.error}>✗</span>}
                <button
                  className="text-ink-faint hover:text-wnv text-[11px] ml-auto"
                  onClick={(e) => {
                    e.preventDefault();
                    const pair = overlayGroupsRef.current[o.layerId];
                    if (pair) {
                      if (mapRef.current) mapRef.current.removeLayer(pair.main);
                      if (noticeMapRef.current) noticeMapRef.current.removeLayer(pair.notice);
                    }
                    delete overlayGroupsRef.current[o.layerId];
                    persistOverlays(overlays.filter((x) => x.layerId !== o.layerId));
                  }}
                >
                  ✕
                </button>
              </label>
            ))}
            <div className="flex flex-col gap-1 px-1 py-1">
              <input value={overlayName} onChange={(e) => setOverlayName(e.target.value)} placeholder="Layer name" className="border border-line-strong rounded px-1.5 py-1 text-[11.5px]" />
              <div className="flex gap-1">
                <input value={overlayId} onChange={(e) => setOverlayId(e.target.value)} placeholder="AGOL item ID" className="flex-1 min-w-0 border border-line-strong rounded px-1.5 py-1 text-[11.5px] font-mono" />
                <button
                  className="rounded px-2 text-[11.5px] font-bold bg-brand-tint text-brand-deep border border-[#bcd6a8]"
                  onClick={() => {
                    if (!overlayName.trim() || !overlayId.trim()) return;
                    persistOverlays([...overlays, { name: overlayName.trim(), layerId: overlayId.trim(), on: false }]);
                    setOverlayName("");
                    setOverlayId("");
                  }}
                >
                  +
                </button>
              </div>
            </div>
            <div className="h-px bg-line my-1.5 mx-0.5" />
            <button onClick={clearArea} className="w-full rounded-lg border border-line-strong py-1.5 text-[12.5px] font-semibold text-ink-soft hover:bg-paper">
              🗑 Clear area
            </button>
          </div>
          {/* Hint */}
          <div className="absolute bottom-4 left-3.5 z-[1000] max-w-[330px] rounded-[10px] px-3.5 py-2.5 text-[12.5px] leading-relaxed text-[#eef2ec]" style={{ background: "rgba(24,33,28,0.86)", backdropFilter: "blur(3px)" }}>
            {mode === "pick" && <><b className="text-white">Pick the subgrid</b> the positive pool sits in — click it to make it the treatment area. Click adjacent cells to extend. Switch to <b className="text-white">Reshape</b> to fine-tune the boundary.</>}
            {mode === "reshape" && <><b className="text-white">Drag vertices</b> to adjust the boundary — they snap to subgrid corners.</>}
            {mode === "draw" && <><b className="text-white">Click to place vertices</b> for a custom polygon; snapping is on. Double-click to finish.</>}
          </div>
        </div>

        {/* Notice composer */}
        <aside className="w-full min-[1000px]:w-[540px] shrink-0 border-l border-line bg-card overflow-y-auto p-4 space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex rounded-lg border border-line-strong overflow-hidden text-[12.5px] font-semibold">
              {(["community", "spray"] as const).map((t) => (
                <button key={t} onClick={() => setMapTemplate(t)} className={`px-3 py-1 ${mapTemplate === t ? "bg-brand text-white" : "bg-card text-ink-soft hover:bg-paper"}`}>
                  {t === "community" ? "Community" : "Spray"}
                </button>
              ))}
            </div>
            <span className="font-mono text-xs text-ink-faint border border-line rounded-full px-2.5 py-0.5">
              {selected.length ? `${selected.length} subgrid${selected.length === 1 ? "" : "s"}` : areaLayerRef.current ? "custom area" : "0 subgrids"} · {acres ? acres.toLocaleString() : "—"} ac
            </span>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <label className="eyebrow !text-ink-soft">
              Treatment date
              <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="mt-1 w-full border border-line-strong rounded-sm2 px-2 py-1.5 text-sm font-sans normal-case tracking-normal text-ink" />
            </label>
            <label className="eyebrow !text-ink-soft">
              Window
              <select
                value={customWindow ? "custom" : timeWindow}
                onChange={(e) => {
                  if (e.target.value === "custom") {
                    setCustomWindow(true);
                    setTimeWindow(`${customStart} - ${customEnd}`);
                  } else {
                    setCustomWindow(false);
                    setTimeWindow(e.target.value);
                  }
                }}
                className="mt-1 w-full border border-line-strong rounded-sm2 px-2 py-1.5 text-sm bg-card font-sans normal-case tracking-normal text-ink"
              >
                {WINDOW_TEMPLATES.map((t) => <option key={t}>{t}</option>)}
                {!customWindow && timeWindow && !WINDOW_TEMPLATES.includes(timeWindow) && (
                  <option key={timeWindow}>{timeWindow}</option>
                )}
                <option value="custom">Custom…</option>
              </select>
              {customWindow && (
                <span className="flex items-center gap-1 mt-1">
                  <input
                    value={customStart}
                    onChange={(e) => { setCustomStart(e.target.value); setTimeWindow(`${e.target.value} - ${customEnd}`); }}
                    className="w-full border border-line-strong rounded-sm2 px-2 py-1 text-sm font-sans normal-case tracking-normal text-ink"
                    placeholder="8:30 PM"
                  />
                  <span className="text-ink-faint">–</span>
                  <input
                    value={customEnd}
                    onChange={(e) => { setCustomEnd(e.target.value); setTimeWindow(`${customStart} - ${e.target.value}`); }}
                    className="w-full border border-line-strong rounded-sm2 px-2 py-1 text-sm font-sans normal-case tracking-normal text-ink"
                    placeholder="12:00 AM"
                  />
                </span>
              )}
            </label>
          </div>

          <div>
            <div className="eyebrow !text-ink-soft mb-1">Reason / footer message</div>
            <div className="flex rounded-sm2 border border-line-strong overflow-hidden">
              {([["wnv", "WNV positive"], ["abundance", "High abundance"]] as [Reason, string][]).map(([r, label]) => (
                <button key={r} onClick={() => { setReason(r as Reason); setCustomBody(null); }} className={`flex-1 py-1.5 text-xs font-semibold ${reason === r && customBody === null ? (r === "wnv" ? "bg-wnv text-white" : "bg-brand text-white") : "bg-card text-ink-soft"}`}>
                  {label}
                </button>
              ))}
              <button
                onClick={() => setCustomBody(c => c === null ? CUSTOM_DEFAULT : null)}
                className={`flex-1 py-1.5 text-xs font-semibold ${customBody !== null ? "bg-amber-500 text-white" : "bg-card text-ink-soft"}`}
              >
                Custom
              </button>
            </div>
            {customBody !== null && (
              <textarea
                value={customBody}
                onChange={(e) => setCustomBody(e.target.value)}
                rows={4}
                className="mt-1.5 w-full border border-line-strong rounded-sm2 px-2 py-1.5 text-xs resize-y"
              />
            )}
          </div>

          <div>
            <div className="eyebrow !text-ink-soft mb-1">Boundary road labels</div>
            <div className="flex gap-2">
              <input
                value={roadInput}
                onChange={(e) => setRoadInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { addRoad(roadInput); setRoadInput(""); } }}
                placeholder="e.g. E Shaw Ave"
                className="flex-1 border border-line-strong rounded-sm2 px-2 py-1.5 text-sm"
              />
              <button
                className="rounded-[7px] px-2.5 py-1.5 text-[12.5px] font-bold whitespace-nowrap bg-brand-tint text-brand-deep border border-[#bcd6a8] hover:bg-[#e4eed9]"
                onClick={() => { addRoad(roadInput); setRoadInput(""); }}
              >
                + Add label
              </button>
              <button
                className="rounded-[7px] px-2.5 py-1.5 text-[12.5px] font-bold whitespace-nowrap bg-info-tint text-info border border-[#b8cdf0] hover:bg-[#dde9fa] disabled:opacity-50"
                onClick={autoLabelRoads}
                disabled={!!busy}
                title="Detect named roads along the treatment-area edges and drop labels on them"
              >
                ✨ Auto
              </button>
            </div>
            {roadChips.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2">
                {roadChips.map((r) => (
                  <span key={r.id} className="inline-flex items-center rounded-full bg-[#fffbe6] border border-[#e6d98a] pl-2 pr-1.5 py-0.5">
                    <input
                      value={r.text}
                      onChange={(e) => renameRoad(r.id, e.target.value)}
                      className="bg-transparent text-[#5b5300] text-xs font-semibold outline-none"
                      style={{ width: `${Math.max(r.text.length, 4) + 1}ch` }}
                    />
                    <button onClick={() => rotateRoad(r.id, -15)} title="Rotate counter-clockwise" className="ml-0.5 text-[#a08a00] hover:text-[#5b5300]">⟲</button>
                    <button onClick={() => rotateRoad(r.id, 15)} title="Rotate clockwise" className="text-[#a08a00] hover:text-[#5b5300]">⟳</button>
                    <button onClick={() => removeRoad(r.id)} title="Remove" className="ml-0.5 text-[#a08a00] hover:text-wnv">✕</button>
                  </span>
                ))}
              </div>
            )}
            <p className="text-ink-faint text-[11px] mt-1.5">Drag to reposition · scroll over a label to rotate it along the street · edit the text in the chips above.</p>
          </div>

          <div className="flex items-center justify-between">
            <span className="eyebrow">Live preview · 8.5 × 11{mapTemplate === "spray" ? " · internal" : ", ready to send"}</span>
            <button className="btn-ghost !py-0.5 !px-2 text-[11px]" onClick={renderNotice} title="Re-center the notice map on the treatment area">
              ⌖ Reset view
            </button>
          </div>

          {/* Community Notice — matches the District print template */}
          <div ref={exportRef} className="mx-auto bg-white" style={{ width: 494, padding: 12, flex: "none" }}>
          {/* noticeRef locked to letter proportions: 470px wide × 608px tall (8.5×11 ratio) */}
          <div ref={noticeRef} className="bg-white flex flex-col" style={{ width: 470, height: 608, border: `2px solid ${black}`, flex: "none", fontFamily: "'PT Sans', sans-serif", position: "relative", overflow: "hidden" }}>
            {/* Header */}
            <div className="flex items-stretch" style={{ borderBottom: `2px solid ${black}`, height: 62, flex: "none" }}>
              <div className="flex flex-col justify-center text-right" style={{ fontFamily: "'PT Serif', serif", fontWeight: 700, fontSize: 22, lineHeight: 1.05, color: black, padding: "6px 12px", borderRight: `2px solid ${black}`, width: 140, flex: "none" }}>
                {mapTemplate === "spray" ? <>Spray<br />Map</> : <>Community<br />Notice</>}
              </div>
              <div className="flex-1 flex flex-col justify-center" style={{ fontFamily: "'PT Sans', sans-serif", padding: "6px 12px" }}>
                {mapTemplate === "spray" ? (
                  <>
                    <div style={{ fontSize: 10, color: "#222" }}>Consolidated Mosquito Abatement District — Internal Use</div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: black, marginTop: 3 }}>{fmtDate}</div>
                    <div style={{ fontSize: 10, color: "#444", marginTop: 1 }}>{timeWindow}</div>
                  </>
                ) : (
                  <>
                    <div style={{ fontSize: 10, color: "#222", whiteSpace: "nowrap" }}>Truck Ultra Low Volume (ULV) treatments are scheduled for:</div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: black, marginTop: 3 }}>{fmtDate}</div>
                  </>
                )}
              </div>
            </div>
            {/* Logo — on noticeRef at z-index above Leaflet tiles; top = header(62) - half logo(26) = 36 */}
            <img src="/logo512.png" alt="CMAD" style={{ position: "absolute", right: 10, top: 36, width: 52, height: 52, zIndex: 700 }} />
            {/* Map — flex:1 fills all remaining vertical space */}
            <div className="relative" style={{ flex: 1, minHeight: 0 }}>
              <div ref={noticeMapDivRef} className="absolute inset-0" style={{ background: "#dfe6d8" }} />
              <div style={{ position: "absolute", right: 5, bottom: 4, zIndex: 600, fontSize: 8, color: "#5b5b5b", fontFamily: "'PT Sans', sans-serif", textAlign: "right", textShadow: "0 0 2px #fff", lineHeight: 1.2, maxWidth: "60%" }}>
                Sources: Map data © OpenStreetMap contributors,<br />Microsoft, Esri Community Maps contributors, Map layer by Esri
              </div>
            </div>
            {/* Footer */}
            {mapTemplate === "spray" ? (
              <div className="flex items-stretch" style={{ borderTop: `2px solid ${black}`, flex: "none" }}>
                <div className="flex flex-col gap-1" style={{ width: 100, flex: "none", borderRight: `2px solid ${black}`, padding: "5px 8px" }}>
                  <div style={{ fontFamily: "'PT Serif', serif", fontWeight: 700, fontSize: 16, color: black }}>Legend</div>
                  <div className="flex items-start gap-1.5" style={{ fontSize: 10 }}>
                    <span style={{ width: 22, height: 28, background: "#cfe0ea", border: "1px solid #9bb6c7", flex: "none", marginTop: 1 }} />
                    <span>Treatment<br />Area</span>
                  </div>
                </div>
                <div className="flex-1" style={{ padding: "5px 10px", display: "flex", flexDirection: "column", gap: 4 }}>
                  <div style={{ fontSize: 10, color: black }}>
                    <span style={{ fontWeight: 700 }}>Subgrid/s: </span>
                    <span style={{ fontFamily: "monospace", fontSize: 9 }}>{noticeLabels.length > 0 ? noticeLabels.join(", ") : "—"}</span>
                  </div>
                  <div style={{ fontSize: 10, color: black }}>
                    <span style={{ fontWeight: 700 }}>Technician/s: </span>
                    <span style={{ borderBottom: "1px solid #999", display: "inline-block", minWidth: 160 }}>&nbsp;</span>
                  </div>
                  <div style={{ fontSize: 10, color: black }}>
                    <span style={{ fontWeight: 700 }}>Product/s: </span>
                    <span style={{ borderBottom: "1px solid #999", display: "inline-block", minWidth: 172 }}>&nbsp;</span>
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex items-stretch" style={{ borderTop: `2px solid ${black}`, flex: "none" }}>
                <div className="flex flex-col gap-1" style={{ width: 100, flex: "none", borderRight: `2px solid ${black}`, padding: "5px 8px" }}>
                  <div style={{ fontFamily: "'PT Serif', serif", fontWeight: 700, fontSize: 16, color: black }}>Legend</div>
                  <div className="flex items-start gap-1.5" style={{ fontSize: 10 }}>
                    <span style={{ width: 22, height: 28, background: "#cfe0ea", border: "1px solid #9bb6c7", flex: "none", marginTop: 1 }} />
                    <span>Treatment<br />Area</span>
                  </div>
                </div>
                <div className="flex-1" style={{ padding: "5px 10px" }}>
                  <div style={{ fontWeight: 700, fontSize: 10, color: black, marginBottom: 1.5 }}>
                    Treatment is scheduled between {winStart} and {winEnd}
                  </div>
                  <div style={{ fontSize: 10, lineHeight: 1.3, color: "#1a1a1a" }}>{customBody !== null ? customBody : BODY[reason]}</div>
                </div>
              </div>
            )}
          </div>
          </div>

          {savedAreas.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-1">
                <div className="eyebrow !text-ink-soft">Saved areas for run ({savedAreas.length})</div>
                <label className="ml-auto flex items-center gap-1.5 text-[11.5px] font-semibold cursor-pointer select-none" title="Draw every saved area on the map at once to review the whole week">
                  <input
                    type="checkbox"
                    checked={showAllAreas}
                    onChange={(e) => setShowAllAreas(e.target.checked)}
                    className="w-3.5 h-3.5 accent-[#7c3aed]"
                  />
                  <span>Show all</span>
                </label>
                <button onClick={fitAllAreas} className="text-[11.5px] font-semibold text-ink-soft hover:text-brand-deep" title="Zoom out to fit every saved area">
                  Fit all
                </button>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {savedAreas.map((a) => (
                  <span
                    key={a.id}
                    className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold ${
                      editingId === a.id ? "bg-brand-tint border-[#bcd6a8] text-brand-deep" : "bg-paper border-line text-ink-soft"
                    }`}
                  >
                    <button onClick={() => loadArea(a)} title="Load for review/editing" className="font-mono">
                      {areaLabel(a)}
                    </button>
                    <span className="text-ink-faint font-normal">{a.acres} ac</span>
                    {a.agolStatus === "Upcoming" ? (
                      <button
                        onClick={() => unscheduleArea(a.id)}
                        className="text-brand-deep hover:text-wnv"
                        title="Scheduled and public — click to call it off. Returns to draft; never recorded as treated."
                      >
                        ● scheduled
                      </button>
                    ) : a.agolStatus === "Completed" ? (
                      <span className="text-ink-faint font-normal" title="Treatment week closed out on AGOL">✓ completed</span>
                    ) : a.syncError ? (
                      <span className="text-wnv" title={`Saved locally only — AGOL write failed: ${a.syncError}`}>▲ local only</span>
                    ) : a.agolObjectId != null ? (
                      <span className="text-ink-faint font-normal" title={`Draft on AGOL (OBJECTID ${a.agolObjectId})`}>◌ draft</span>
                    ) : (
                      <span className="text-ink-faint font-normal" title="Not mirrored to AGOL yet">◌ local</span>
                    )}
                    <button onClick={() => removeArea(a.id)} title="Remove" className="text-ink-faint hover:text-wnv">✕</button>
                  </span>
                ))}
              </div>
            </div>
          )}

          <div className="flex gap-2.5">
            <button onClick={finalize} className="btn-ghost flex-1 !py-2.5 text-[13.5px] font-bold">
              {editingId ? "Update saved area" : "Save area for Run"}
            </button>
            <button onClick={() => exportNotice("png")} className="btn-ghost flex-1 !py-2.5 text-[13.5px] font-bold" disabled={!!busy}>⬇ Export PNG</button>
            <button onClick={() => exportNotice("pdf")} className="btn-primary flex-1 !py-2.5 text-[13.5px] font-bold" disabled={!!busy}>📄 Export PDF</button>
          </div>
          <button
            onClick={scheduleForTreatment}
            className="btn-primary w-full !py-2.5 text-[13px] font-bold"
            disabled={!!busy || !draftCount}
            title="Flip every draft area to Upcoming on the Adulticide Treatment Areas layer — no ArcGIS Pro round-trip"
          >
            📅 Schedule {draftCount || ""} area{draftCount === 1 ? "" : "s"} for treatment
          </button>
          <button
            onClick={exportAllNotices}
            className="btn-ghost w-full !py-2 text-[12.5px] font-bold"
            disabled={!!busy || !savedAreas.length}
            title="Export one PNG per saved area, each with its own road labels and footer. Your browser may ask once to allow multiple downloads."
          >
            ⬇ Export all {savedAreas.length} notice PNGs
          </button>
          <div className="flex gap-2">
            <button onClick={() => downloadShapefile("active")} className="btn-ghost flex-1 !py-2 text-[12.5px] font-bold" disabled={!!busy} title="Download just the treatment area currently on the map as a zipped shapefile">
              🗺️ .shp (this area)
            </button>
            <button
              onClick={() => downloadShapefile("all")}
              className="btn-ghost flex-1 !py-2 text-[12.5px] font-bold"
              disabled={!!busy || !savedAreas.length}
              title="Download every saved area for the run as one zipped shapefile — no need to redraw them"
            >
              🗺️ .shp (all {savedAreas.length})
            </button>
          </div>
          <p className="text-ink-faint text-[11px] pb-6">
            Finalized areas + date/time flow into Run Step 5 (publish) and Step 8 (mark treated).
          </p>
        </aside>
      </div>
    </div>
  );
}
